/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { TIMESLIDER_HEIGHT } from '../SoundingsTimeSlider/SoundingsTimeSlider';

const OFFSET_HEIGHT = 100; // Space reserved in layout

export const useSoundingsDiagramSize = (
  ref: React.RefObject<HTMLDivElement | null>,
  useWidth = true,
): number => {
  const [size, setSize] = React.useState(0);

  React.useLayoutEffect(() => {
    const container = ref.current;
    if (!container) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      if (!entries.length) {
        return;
      }
      const { width, height } = entries[0].contentRect;
      const availableHeight = height - TIMESLIDER_HEIGHT + OFFSET_HEIGHT;
      const nextSize = useWidth
        ? Math.floor(Math.min(width, availableHeight))
        : Math.floor(availableHeight);
      setSize(nextSize > 0 ? nextSize : 0);
    });

    resizeObserver.observe(container);
    return (): void => {
      resizeObserver.disconnect();
    };
  }, [ref, useWidth]);

  return size;
};
